import React from 'react';
import dayjs from 'dayjs';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { capabilityGate } from '@hopdrive/feature-portal-products';
import { useProductEntry } from '@features/products/useProductEntry';

import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  Typography,
  useTheme,
} from '@mui/material';
import {
  BiMap,
  BiTime,
  BiCar,
  BiPhone,
  BiPencil,
  BiTrash,
  BiChevronUp,
  BiChevronDown,
  BiLockAlt,
  BiGridVertical,
  BiShow,
  BiPlus,
  BiCalendar,
} from 'react-icons/bi';
import { toast } from 'react-toastify';

import { useGetServiceRunDetailSnapshotQuery } from '../queries/getServiceRunDetail.query.generated';
import { useGetServiceRunDetailSubscription } from '../queries/getServiceRunDetail.subscription.generated';
import { useRooftop } from '@store';
import Toolbar from '@components/Utils/Toolbar';
import RunMapView from '@features/mapVisualization/components/RunMapView';
import { classifyRunStopStatus } from '@features/mapVisualization/runTypes';
import type { RunMapStop } from '@features/mapVisualization/runTypes';
import { useRunLegPolylines } from '../hooks/useRunLegPolylines';
import { useRunEditMode, UNEDITABLE_RUN_STATUSES, STOP_FIELD_LABELS } from '../hooks/useRunEditMode';
import type { StopFieldValues } from '../hooks/useRunEditMode';
import StopDetailDrawer from '../components/StopDetailDrawer';
import RunDetailOffer from '@components/RunDetailOffer';
import { useGetOfferedDriverQuery } from '../queries/getOfferedDriver.query.generated';
import RunDriverMarker from '../components/RunDriverMarker';
import ReassignDriverDialog from '../components/ReassignDriverDialog';
import UnassignDriverDialog from '../components/UnassignDriverDialog';
import RunEditReviewDialog from '../components/RunEditReviewDialog';
import RunStopEditForm from '../components/RunStopEditForm';
import AddStopPickerDialog from '../components/AddStopPickerDialog';
import type { PickableAppointment } from '../components/AddStopPickerDialog';
import RunStatusChip from '@components/Badges/RunStatusChip';

// ── Helpers ---------------------------------------------------------------- //

const MAP_ID = 'run-detail-map';

const stopStatusColors: Record<string, string> = {
  pending_confirmation: 'warning',
  confirmed: 'info',
  assigned: 'info',
  in_transit_to: 'warning',
  arrived: 'primary',
  in_service: 'primary',
  service_in_progress: 'primary',
  completed: 'success',
  skipped: 'error',
};

const stopStatusLabels: Record<string, string> = {
  pending_confirmation: 'Awaiting Confirmation',
};

const formatStatus = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const formatTime = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// ── Component -------------------------------------------------------------- //

const RunDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const rooftop = useRooftop();
  // Managing a run (dispatch, edit, assign/reassign) is gated on the Mobile Service product's
  // manage capability; a read_only product shows the run for viewing only.
  const canManage = capabilityGate(useProductEntry('mobile-service'), 'manageExisting').kind === 'render';

  // Query-first, subscription-second (both select ServiceRunDetailFields).
  // The HTTP query gives a fast first paint and still works when the
  // websocket is down or mid-handshake; the subscription supersedes it for
  // live updates once the socket delivers. Rendering was previously gated on
  // the subscription alone, which meant 5s+ initial loads (socket connect +
  // Hasura live-query snapshot) and a blank page whenever the socket was dead.
  // A malformed :id (e.g. /runs/abc) would send NaN to Hasura and get an
  // error back rather than a graceful empty result — skip instead, which
  // falls through to the "Run not found" state.
  const runId = parseInt(id ?? '', 10);
  const { data: queryData, loading: queryLoading, error: queryError } = useGetServiceRunDetailSnapshotQuery({
    variables: { runId },
    fetchPolicy: 'cache-and-network',
    skip: Number.isNaN(runId),
  });
  const { data: subData } = useGetServiceRunDetailSubscription({
    variables: { runId },
    skip: Number.isNaN(runId),
  });

  // Once the subscription has delivered anything, trust it entirely —
  // including a null by_pk (run deleted mid-session). Unwrapping with ?? on
  // the inner field would fall back to the stale cached query run forever.
  const run = subData ? subData.mobility_runs_by_pk : queryData?.mobility_runs_by_pk;
  const loading = !run && queryLoading;
  // Only surface an error when there's truly nothing to show — under
  // cache-and-network a cached `run` can coexist with a stale `queryError`
  // from an earlier failed refetch.
  const error = queryLoading || run ? undefined : queryError;
  const stops = run?.stops ?? [];
  const customerId = run?.customer_id || rooftop?.id;

  // The active bundle for this run, and its offers.
  const activeBundle = run?.scenario?.bundletoscenarios?.[0]?.bundle ?? null;
  // All active offers for this run (from the active bundle)
  const activeOffers = activeBundle?.offers ?? [];
  // Whether the run can be assigned/unassigned
  const canAssignRun = run?.status === 'dispatched' || run?.status === 'claimed';
  const acceptedOffer = activeOffers.find(o => o.status === 'accepted' || o.status === 'assigned');

  // Targeted-dispatch fallback: when neither run.driver nor an offer row names a driver
  // yet, bundle.config.driverIdOverride is the synchronous record of who the dispatch went
  // to (the offers row lands seconds later via an async bundle→batchjob chain, or never on
  // a dropped hop). Resolve that id so the panel reads "Dispatching…" during the gap.
  const offeredDriver = activeOffers[0]?.driver ?? null;
  const pendingTargetId =
    !run?.driver && !offeredDriver
      ? Number((activeBundle?.config as { driverIdOverride?: unknown })?.driverIdOverride) || null
      : null;
  const { data: pendingDriverData } = useGetOfferedDriverQuery({
    variables: { driverId: pendingTargetId ?? 0 },
    skip: !pendingTargetId,
  });
  const pendingDriver = pendingTargetId ? pendingDriverData?.drivers_by_pk ?? null : null;

  // Who the run is with, for the unassign dialog and the map marker: an accepted offer or
  // the run's direct driver, else the offered driver, else the targeted dispatch still
  // landing — the same set hasActiveDriver counts, so a run whose action reads Unassign
  // always names someone. Same fallbacks as embed's copy of this page.
  const displayDriver = acceptedOffer?.driver ?? run?.driver ?? offeredDriver ?? pendingDriver ?? null;

  // Same semantics as MoveDetailsOffer's hasDriver: a committed driver, an in-flight offer,
  // or a targeted dispatch still landing all mean the action is "Unassign".
  const hasActiveDriver = !!(run?.driver || activeOffers.length || pendingDriver);

  // ── Run edit mode (docs/run-edit-mode-design.md) ── //
  // All edits stage into a draft; nothing mutates until the coordinator
  // reviews and applies. The subscription delivers the post-apply data.
  const editableStops = React.useMemo(
    () =>
      stops.map(s => {
        const detail = s.service_stop_detail?.[0];
        const order = s.service_orders?.[0];
        return {
          id: s.id,
          status: s.status,
          serviceOrderId: order?.id ?? null,
          fields: {
            customer_name: detail?.customer_name ?? '',
            customer_phone: detail?.customer_phone ?? '',
            service_description: detail?.service_description ?? '',
            address: s.location?.address || s.address || '',
            address_lat: s.location?.latitude ?? s.address_lat ?? null,
            address_lng: s.location?.longitude ?? s.address_lng ?? null,
            location_id: s.location?.id ?? null,
            vehicle_year: order?.vehicle_year ?? null,
            vehicle_make: order?.vehicle_make ?? '',
            vehicle_model: order?.vehicle_model ?? '',
          },
        };
      }),
    [stops]
  );
  const runEdit = useRunEditMode({
    runId: run?.id ?? null,
    stops: editableStops,
    onApplied: ({ removed, reordered, edited, added }) => {
      const parts: string[] = [];
      if (added > 0) parts.push(`${added} stop${added === 1 ? '' : 's'} added`);
      if (reordered) parts.push('stops reordered');
      if (edited > 0) parts.push(`${edited} stop${edited === 1 ? '' : 's'} edited`);
      if (removed > 0) parts.push(`${removed} stop${removed === 1 ? '' : 's'} returned to the queue`);
      // No fixed toastId: react-toastify drops a toast whose id is still on screen, which
      // swallowed the confirmation for a second Apply made within autoClose (#3968).
      toast.success(`Run updated — ${parts.join(', ')}`);
    },
  });
  const [reviewOpen, setReviewOpen] = React.useState(false);
  const [dragStopId, setDragStopId] = React.useState<number | null>(null);
  const [dropTargetId, setDropTargetId] = React.useState<number | null>(null);
  // Which stop's inline edit form is open (edit mode only).
  const [editingStopFormId, setEditingStopFormId] = React.useState<number | null>(null);
  // Add-stop picker (phase 3): staged appointments keyed by their temp stop id.
  const [addStopPickerOpen, setAddStopPickerOpen] = React.useState(false);
  const [addedAppointments, setAddedAppointments] = React.useState<Record<number, PickableAppointment>>({});

  // Render a staged appointment as a stop-shaped object so the existing card
  // and map plumbing work unchanged.
  const syntheticStopFor = React.useCallback((tempId: number, appt: PickableAppointment) => {
    const estimatedMinutes = Number((appt.config as { estimated_service_minutes?: unknown })?.estimated_service_minutes) || null;
    return {
      id: tempId,
      stop_order: 0,
      status: 'pending_confirmation',
      address: appt.pickup?.address ?? null,
      address_lat: appt.pickup?.latitude ?? null,
      address_lng: appt.pickup?.longitude ?? null,
      notes: appt.driver_notes ?? null,
      location: appt.pickup
        ? {
            id: appt.pickup.id,
            name: appt.pickup.name,
            address: appt.pickup.address,
            latitude: appt.pickup.latitude,
            longitude: appt.pickup.longitude,
            geofence_radius: null,
            geofence_vertices: null,
            geofence_center_lat: null,
            geofence_center_lng: null,
          }
        : null,
      service_stop_detail: [{
        customer_name: appt.consumer_name,
        customer_phone: appt.consumer_phone,
        service_description: appt.driver_notes,
        ro_reference: appt.ro_number,
        estimated_service_minutes: estimatedMinutes,
        is_fleet_stop: false,
      }],
      service_orders: [{
        id: tempId,
        vehicle_vin: appt.vehicle_vin,
        vehicle_make: appt.vehicle_make,
        vehicle_model: appt.vehicle_model,
        vehicle_year: appt.vehicle_year,
        vehicle_color: appt.vehicle_color,
        service_description: appt.driver_notes || 'Mobile service',
        status: 'pending',
        workflow_data: {},
        vehiclephotos: [],
      }],
    } as unknown as (typeof stops)[number];
    // The cast references `stops` for its element type only — no runtime dep.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // While editing, the list and map render the draft order (minus removals),
  // with staged field edits overlaid so cards and markers update live.
  const displayStops = React.useMemo(() => {
    if (!runEdit.editing) return stops;
    const byId = new Map(stops.map(s => [s.id, s]));
    return runEdit.draftOrder
      .map(id => {
        if (id < 0) {
          const appt = addedAppointments[id];
          return appt ? syntheticStopFor(id, appt) : undefined;
        }
        return byId.get(id);
      })
      .filter((s): s is (typeof stops)[number] => Boolean(s))
      .map(s => {
        const changed = runEdit.changedFieldsFor(s.id);
        if (Object.keys(changed).length === 0) return s;
        const staged = runEdit.stagedFieldsFor(s.id)!;
        const detail = s.service_stop_detail?.[0];
        const [firstOrder, ...restOrders] = s.service_orders ?? [];
        const addressChanged = 'address' in changed;
        return {
          ...s,
          address: staged.address,
          address_lat: staged.address_lat,
          address_lng: staged.address_lng,
          // A staged address points at a different location — drop the stale
          // location object so the overridden address/coords win everywhere.
          location: addressChanged ? null : s.location,
          service_stop_detail: [{
            ...detail,
            customer_name: staged.customer_name,
            customer_phone: staged.customer_phone,
            service_description: staged.service_description,
          }],
          service_orders: firstOrder
            ? [{
                ...firstOrder,
                vehicle_year: staged.vehicle_year,
                vehicle_make: staged.vehicle_make,
                vehicle_model: staged.vehicle_model,
              }, ...restOrders]
            : s.service_orders,
        } as typeof s;
      });
  }, [stops, runEdit.editing, runEdit.draftOrder, runEdit.changedFieldsFor, runEdit.stagedFieldsFor, addedAppointments, syntheticStopFor]);

  const canEditRun = !!run && !UNEDITABLE_RUN_STATUSES.has(run.status) && canManage;
  const isDispatched = !!run && run.status !== 'draft' && run.status !== 'scheduled';

  const stopDisplayTitle = React.useCallback((stop: (typeof stops)[number]) => {
    return stop.service_stop_detail?.[0]?.customer_name || stop.notes || stop.location?.name || 'Unknown';
  }, []);

  const removedStopSummaries = React.useMemo(
    () =>
      runEdit.removedIds
        .map(id => stops.find(s => s.id === id))
        .filter((s): s is (typeof stops)[number] => Boolean(s))
        .map(s => ({
          id: s.id,
          label: stopDisplayTitle(s),
          address: s.location?.address || s.address || null,
        })),
    [runEdit.removedIds, stops, stopDisplayTitle]
  );

  // Field-level diffs for the review dialog. The address triplet
  // (lat/lng/location_id) collapses into the single visible "Address" change.
  const editedStopSummaries = React.useMemo(
    () =>
      runEdit.editedStopIds.map(id => {
        const stop = stops.find(s => s.id === id);
        const changed = runEdit.changedFieldsFor(id);
        const baseline = editableStops.find(s => s.id === id)?.fields;
        const seen = new Set<string>();
        const changes = (Object.keys(changed) as (keyof StopFieldValues)[])
          .filter(k => k !== 'address_lat' && k !== 'address_lng' && k !== 'location_id')
          .map(k => {
            const label = STOP_FIELD_LABELS[k];
            if (seen.has(label)) return null;
            seen.add(label);
            return {
              field: label,
              from: String(baseline?.[k] ?? ''),
              to: String(changed[k] ?? ''),
            };
          })
          .filter((c): c is { field: string; from: string; to: string } => c !== null);
        return {
          id,
          label: stop ? stopDisplayTitle(stop) : `Stop ${id}`,
          changes,
        };
      }),
    [runEdit.editedStopIds, runEdit.changedFieldsFor, stops, editableStops, stopDisplayTitle]
  );

  // Staged additions for the review dialog, in draft order.
  const addedStopSummaries = React.useMemo(
    () =>
      runEdit.draftOrder
        .filter(id => id < 0)
        .map(id => {
          const appt = addedAppointments[id];
          return {
            id,
            label: appt?.consumer_name || 'New stop',
            address: appt?.pickup?.address ?? null,
          };
        }),
    [runEdit.draftOrder, addedAppointments]
  );

  // Leaving edit mode (apply/discard) closes any open inline form and drops
  // staged-addition state.
  React.useEffect(() => {
    if (!runEdit.editing) {
      setEditingStopFormId(null);
      setAddStopPickerOpen(false);
      setAddedAppointments({});
    }
  }, [runEdit.editing]);

  // Measure remaining viewport height for the content area
  const contentRef = React.useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = React.useState<string>('100%');
  React.useEffect(() => {
    const measure = () => {
      if (contentRef.current) {
        const top = contentRef.current.getBoundingClientRect().top;
        setContentHeight(`${window.innerHeight - top}px`);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [run, runEdit.editing]);

  // Compute map bounds from stops
  const mapBounds = React.useMemo(() => {
    const lats: number[] = [];
    const lngs: number[] = [];
    displayStops.forEach(s => {
      const lat = s.location?.latitude ?? s.address_lat;
      const lng = s.location?.longitude ?? s.address_lng;
      if (lat && lng) {
        lats.push(lat);
        lngs.push(lng);
      }
    });
    if (lats.length === 0) return null;
    const padding = 0.01;
    return {
      north: Math.max(...lats) + padding,
      south: Math.min(...lats) - padding,
      east: Math.max(...lngs) + padding,
      west: Math.min(...lngs) - padding,
    };
  }, [displayStops]);

  // Transform stops into map-renderable format. Draft order while editing —
  // stopOrder is the draft position so markers renumber live.
  const mapStops: RunMapStop[] = React.useMemo(() => {
    return displayStops
      .filter(s => {
        const lat = s.location?.latitude ?? s.address_lat;
        const lng = s.location?.longitude ?? s.address_lng;
        return lat != null && lng != null;
      })
      .map(s => {
        const detail = s.service_stop_detail?.[0];
        return {
          id: s.id,
          stopOrder: runEdit.editing ? displayStops.indexOf(s) + 1 : s.stop_order,
          status: s.status,
          phase: classifyRunStopStatus(s.status),
          address: s.location?.address || s.address,
          lat: (s.location?.latitude ?? s.address_lat)!,
          lng: (s.location?.longitude ?? s.address_lng)!,
          location: s.location ? {
            id: s.location.id,
            name: s.location.name,
            address: s.location.address,
            latitude: s.location.latitude,
            longitude: s.location.longitude,
            geofence_radius: s.location.geofence_radius,
            geofence_vertices: s.location.geofence_vertices as Array<{ lat: number; lng: number }> | null,
            geofence_center_lat: s.location.geofence_center_lat,
            geofence_center_lng: s.location.geofence_center_lng,
          } : null,
          customerName: detail?.customer_name || s.notes || s.location?.name,
          customerPhone: detail?.customer_phone,
          serviceDescription: detail?.service_description,
          estimatedServiceMinutes: detail?.estimated_service_minutes,
        };
      });
  }, [displayStops, runEdit.editing]);

  // Build leg pairs for lane polyline fetching
  const legs = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < stops.length - 1; i++) {
      result.push({
        fromLocationId: stops[i].location?.id ?? null,
        toLocationId: stops[i + 1].location?.id ?? null,
      });
    }
    return result;
  }, [stops]);

  const { legPolylines } = useRunLegPolylines(customerId, legs);

  // Stop selection for fly-to zoom + detail drawer.
  //
  // Initial state can come from a `?stop=N` query param so deep links (e.g.
  // the Run column on the appointments table) open the drawer for a
  // specific stop on arrival. Computed once on mount — subsequent URL
  // changes don't reopen the drawer the user has closed.
  const [searchParams] = useSearchParams();
  const initialStopOrder = React.useMemo<number | null>(() => {
    const raw = searchParams.get('stop');
    if (!raw) return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : null;
    // Suppression is intentional: searchParams is read once on mount so a
    // later URL change can't reopen a drawer the user already closed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedStopOrder, setSelectedStopOrder] = React.useState<number | null>(initialStopOrder);
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(initialStopOrder !== null);
  const [reassignDialogOpen, setReassignDialogOpen] = React.useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = React.useState(false);

  const handleStopClick = React.useCallback((stopOrder: number) => {
    setSelectedStopOrder(prev => {
      const next = prev === stopOrder ? null : stopOrder;
      setDrawerOpen(next !== null);
      return next;
    });
  }, []);

  const handleDrawerClose = React.useCallback(() => {
    setDrawerOpen(false);
    setSelectedStopOrder(null);
  }, []);

  const selectedStop = React.useMemo(() => {
    if (selectedStopOrder === null) return null;
    return stops.find(s => s.stop_order === selectedStopOrder) ?? null;
  }, [selectedStopOrder, stops]);

  // Extract workflow steps from the workflowset's mobile inspection workflow
  const workflowSteps = React.useMemo(() => {
    const steps = run?.profile?.mobileInspectionWorkflow?.steps;
    if (!steps || !Array.isArray(steps)) return null;
    return steps;
  }, [run]);

  // Compute fly-to bounds from the selected stop + its adjacent stops
  const flyToBounds = React.useMemo<google.maps.LatLngBoundsLiteral | null>(() => {
    if (selectedStopOrder === null) return null;
    const idx = stops.findIndex(s => s.stop_order === selectedStopOrder);
    if (idx < 0) return null;

    const relevantStops = [stops[idx]];
    if (idx > 0) relevantStops.push(stops[idx - 1]);
    if (idx < stops.length - 1) relevantStops.push(stops[idx + 1]);

    const lats: number[] = [];
    const lngs: number[] = [];
    relevantStops.forEach(s => {
      const lat = s.location?.latitude ?? s.address_lat;
      const lng = s.location?.longitude ?? s.address_lng;
      if (lat && lng) { lats.push(lat); lngs.push(lng); }
    });
    if (lats.length === 0) return null;
    return {
      north: Math.max(...lats) + 0.005,
      south: Math.min(...lats) - 0.005,
      east: Math.max(...lngs) + 0.005,
      west: Math.min(...lngs) - 0.005,
    };
  }, [selectedStopOrder, stops]);

  const defaultCenter = mapBounds
    ? { lat: (mapBounds.north + mapBounds.south) / 2, lng: (mapBounds.east + mapBounds.west) / 2 }
    : { lat: 33.749, lng: -84.388 }; // Atlanta fallback

  return (
    <>
      {/* ── Header ── */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Toolbar
          fullscreen
          zIndex={0}
          position='relative'
          title={run ? `Run #${run.id}` : `Run #${id}`}
          back={() => navigate('/mobile-service/runs')}
        />
      </Box>

      {/* ── Edit mode banner ── */}
      {runEdit.editing ? (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: '#202430',
          color: '#fff',
          px: 2.5,
          py: 1,
          flexShrink: 0,
        }}>
          <BiPencil size={14} style={{ opacity: 0.7, flexShrink: 0 }} />
          <Typography variant='caption' sx={{ opacity: 0.92, fontWeight: 500 }}>
            Editing run — changes aren't live until you review and apply them.
          </Typography>
        </Box>
      ) : null}

      {loading ? <LinearProgress /> : null}

      {error ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color='error'>Failed to load run details.</Typography>
        </Box>
      ) : null}

      {!loading && !error && !run ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color='text.secondary'>Run not found.</Typography>
        </Box>
      ) : null}

      <StopDetailDrawer
        open={drawerOpen && !runEdit.editing}
        stop={selectedStop as any}
        workflowSteps={workflowSteps}
        totalStops={stops.length}
        onClose={handleDrawerClose}
      />

      {run ? (
        <>
          <ReassignDriverDialog
            open={reassignDialogOpen}
            onClose={() => setReassignDialogOpen(false)}
            runId={run.id}
            currentDriverId={displayDriver?.id ?? null}
            currentDriverName={displayDriver?.user?.display_name ?? null}
          />
          <UnassignDriverDialog
            open={unassignDialogOpen}
            onClose={() => setUnassignDialogOpen(false)}
            runId={run.id}
            currentDriverName={displayDriver?.user?.display_name ?? null}
          />
          <RunEditReviewDialog
            open={reviewOpen}
            onClose={() => setReviewOpen(false)}
            onApply={async () => {
              const ok = await runEdit.apply();
              if (ok) setReviewOpen(false);
            }}
            applying={runEdit.applying}
            applyError={runEdit.applyError}
            isDispatched={isDispatched}
            driverName={displayDriver?.user?.display_name ?? null}
            orderChanged={runEdit.orderChanged}
            newOrderLabels={displayStops.map((s, i) => `${i + 1}. ${stopDisplayTitle(s)}`)}
            removedStops={removedStopSummaries}
            editedStops={editedStopSummaries}
            addedStops={addedStopSummaries}
          />
          <AddStopPickerDialog
            open={addStopPickerOpen}
            onClose={() => setAddStopPickerOpen(false)}
            customerId={customerId ?? null}
            excludeAppointmentIds={Object.values(runEdit.addedStops).map(a => a.appointmentId)}
            onPick={appt => {
              const serviceWindow = appt.appointment_time
                ? dayjs(appt.appointment_time).format('MMM D, h:mm A')
                : null;
              const tempId = runEdit.addStopFromAppointment(appt.id, serviceWindow);
              setAddedAppointments(prev => ({ ...prev, [tempId]: appt }));
              setAddStopPickerOpen(false);
            }}
          />
        </>
      ) : null}

      {run ? (
        <Box ref={contentRef} sx={{ display: 'flex', overflow: 'hidden', height: contentHeight }}>
          {/* ── Left panel: run info + stops ── */}
          <Box sx={{ width: 460, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
           <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            {/* Run metadata */}
            <Box sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <RunStatusChip status={run.status} sx={{ fontWeight: 600 }} />
                <Typography variant='caption' color='text.secondary'>
                  {stops.length} stop{stops.length !== 1 ? 's' : ''}
                  {run.estimated_distance_miles ? ` · ${run.estimated_distance_miles} mi` : ''}
                </Typography>
                {/* Dispatch affordance — only when the run is parked. Reuses
                    ReassignDriverDialog (with currentDriverId=null it adapts
                    its copy to "Dispatch run / Pick a technician"). The
                    server's reassign endpoint handles draft → dispatched in
                    the same flow as a regular reassign. */}
                {run.status === 'draft' && !runEdit.editing && canManage && (
                  <Button
                    size='small'
                    variant='contained'
                    onClick={() => setReassignDialogOpen(true)}
                    sx={{ ml: 'auto', textTransform: 'none', fontSize: 12 }}>
                    Dispatch
                  </Button>
                )}
                {canEditRun && !runEdit.editing && (
                  <Button
                    size='small'
                    variant='outlined'
                    startIcon={<BiPencil size={13} />}
                    onClick={runEdit.enterEdit}
                    sx={{
                      ml: run.status === 'draft' ? 0 : 'auto',
                      textTransform: 'none',
                      fontSize: 12,
                      flexShrink: 0,
                    }}>
                    Edit run
                  </Button>
                )}
              </Box>

              {/* Driver assignment panel — mirrors MoveDetailsOffer. The single
                  action button matches the moves pattern: "Unassign" (confirm
                  dialog) when a driver/offer is active, "Assign Driver" (pick
                  a technician) otherwise. */}
              <RunDetailOffer
                offers={activeOffers}
                runDriver={run.driver}
                pendingDriver={pendingDriver}
                canAssign={canAssignRun}
                onAssignOrUnassign={() =>
                  hasActiveDriver ? setUnassignDialogOpen(true) : setReassignDialogOpen(true)
                }
                // One-step swap: the same dialog, in reassign mode (currentDriverId set), so the
                // run goes claimed/dispatched -> dispatched and never parks through draft.
                onReassign={() => setReassignDialogOpen(true)}
                // Stuck run: dispatched with no driver, offer or pending target showing.
                onUnassignWithoutDriver={canAssignRun && !hasActiveDriver ? () => setUnassignDialogOpen(true) : undefined}
              />

              {/* Vehicle */}
              {run.vehicle ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <BiCar size={16} color={theme.palette.text.secondary} />
                  <Typography variant='body2'>
                    {[run.vehicle.year, run.vehicle.make, run.vehicle.model].filter(Boolean).join(' ')}
                    {run.vehicle.license_plate ? ` · ${run.vehicle.license_plate}` : ''}
                  </Typography>
                </Box>
              ) : null}

              {/* Timing */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <BiTime size={16} color={theme.palette.text.secondary} />
                <Typography variant='body2'>
                  Departure: {formatTime(run.departure_time)}
                </Typography>
              </Box>
              {run.dispatched_at ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <BiTime size={16} color={theme.palette.text.secondary} />
                  <Typography variant='body2'>
                    Dispatched: {formatTime(run.dispatched_at)}
                  </Typography>
                </Box>
              ) : null}
              {run.completed_at ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <BiTime size={16} color={theme.palette.success.main} />
                  <Typography variant='body2'>
                    Completed: {formatTime(run.completed_at)}
                  </Typography>
                </Box>
              ) : null}
            </Box>

            <Divider />

            {/* Stops list */}
            <Box sx={{ p: 2.5 }}>
              <Typography variant='overline' sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1, display: 'block', mb: 1.5 }}>
                Stops ({displayStops.length})
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {displayStops.map((stop, idx) => {
                  const detail = stop.service_stop_detail?.[0];
                  const order = stop.service_orders?.[0];
                  const loc = stop.location;
                  const address = loc?.address || stop.address || 'No address';

                  const displayOrder = runEdit.editing ? idx + 1 : stop.stop_order;
                  const isSelected = !runEdit.editing && selectedStopOrder === stop.stop_order;
                  const isDealership = runEdit.editing
                    ? runEdit.isAnchorStop(stop.id)
                    : stop.stop_order === 1 || stop.stop_order === stops.length;
                  const isLockedStop = runEdit.isLockedStop(stop.id);
                  const isStopCompleted = stop.status === 'completed';
                  const canMove = runEdit.editing && runEdit.isMovable(stop.id);
                  const movableIdxs = displayStops
                    .map((s, i2) => (runEdit.isMovable(s.id) ? i2 : -1))
                    .filter(i2 => i2 >= 0);
                  const firstMovable = movableIdxs[0];
                  const lastMovable = movableIdxs[movableIdxs.length - 1];
                  const isDropTarget = runEdit.editing && dropTargetId === stop.id && dragStopId !== null && dragStopId !== stop.id;
                  const isMutedCard = runEdit.editing && (isLockedStop || isDealership);
                  const isAddedNew = stop.id < 0;
                  const isFormOpen = runEdit.editing && editingStopFormId === stop.id;

                  return (
                    <Card
                      key={stop.id}
                      elevation={0}
                      onClick={runEdit.editing ? undefined : () => handleStopClick(stop.stop_order)}
                      draggable={canMove && editingStopFormId !== stop.id}
                      onDragStart={canMove ? (e: React.DragEvent) => {
                        setDragStopId(stop.id);
                        e.dataTransfer.effectAllowed = 'move';
                      } : undefined}
                      onDragEnd={canMove ? () => {
                        setDragStopId(null);
                        setDropTargetId(null);
                      } : undefined}
                      onDragOver={runEdit.editing ? (e: React.DragEvent) => {
                        if (dragStopId == null || dragStopId === stop.id || !runEdit.isMovable(stop.id)) return;
                        e.preventDefault();
                        setDropTargetId(stop.id);
                      } : undefined}
                      onDragLeave={runEdit.editing ? () => {
                        setDropTargetId(prev => (prev === stop.id ? null : prev));
                      } : undefined}
                      onDrop={runEdit.editing ? (e: React.DragEvent) => {
                        e.preventDefault();
                        if (dragStopId != null) runEdit.moveStopTo(dragStopId, stop.id);
                        setDragStopId(null);
                        setDropTargetId(null);
                      } : undefined}
                      sx={{
                        overflow: 'visible',
                        cursor: runEdit.editing ? (canMove ? 'grab' : 'default') : 'pointer',
                        border: 'none',
                        bgcolor: isMutedCard ? 'background.default' : 'background.paper',
                        opacity: dragStopId === stop.id ? 0.4 : 1,
                        outline: (isSelected || isDropTarget || (runEdit.editing && editingStopFormId === stop.id))
                          ? `2px solid ${theme.palette.primary.main}`
                          : 'none',
                        outlineOffset: (isSelected || isDropTarget || (runEdit.editing && editingStopFormId === stop.id)) ? 1 : 0,
                        transition: 'box-shadow 0.15s, transform 0.15s, outline 0.15s, opacity 0.15s',
                        transform: isSelected ? 'translateY(-2px)' : 'none',
                        boxShadow: isMutedCard
                          ? 'none'
                          : isSelected
                            ? '0px 8px 24px rgba(0,0,0,0.10), 0px 4px 8px rgba(0,0,0,0.05)'   // level 3
                            : '0px 4px 12px rgba(0,0,0,0.08), 0px 2px 4px rgba(0,0,0,0.04)',   // level 2
                        '&:hover': runEdit.editing ? {} : {
                          transform: 'translateY(-2px)',
                          boxShadow: '0px 8px 24px rgba(0,0,0,0.10), 0px 4px 8px rgba(0,0,0,0.05)', // level 3
                        },
                      }}
                    >
                     {isFormOpen ? (
                      /* ── Edit layout replaces the card body (per comp) ── */
                      <Box sx={{ px: 2, py: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: 'secondaryAccent.main', fontSize: 13, fontWeight: 700 }}>
                            {displayOrder}
                          </Avatar>
                          <Typography variant='body2' sx={{ fontWeight: 600, flex: 1 }} noWrap>
                            {detail?.customer_name || stop.notes || loc?.name || 'Unknown'}
                          </Typography>
                        </Box>
                        <Divider sx={{ my: 1.5 }} />
                        <RunStopEditForm
                          stopOrder={displayOrder}
                          values={runEdit.stagedFieldsFor(stop.id)!}
                          customerId={customerId ?? null}
                          hasChanges={Object.keys(runEdit.changedFieldsFor(stop.id)).length > 0}
                          onChange={patch => runEdit.setStopFields(stop.id, patch)}
                          onRevert={() => runEdit.revertStopFields(stop.id)}
                          onRemove={() => {
                            setEditingStopFormId(null);
                            runEdit.removeStop(stop.id);
                          }}
                          onDone={() => setEditingStopFormId(null)}
                        />
                      </Box>
                     ) : (
                     <Box sx={{ display: 'flex' }}>
                      {runEdit.editing && canMove ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', pl: 0.75, color: 'grey.400' }} title='Drag to reorder'>
                          <BiGridVertical size={16} />
                        </Box>
                      ) : null}
                      <Box sx={{ px: runEdit.editing && canMove ? 1.25 : 2, py: 1.5, flex: 1, minWidth: 0 }}>
                        {/* Stop header */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                          <Avatar
                            sx={{
                              width: 28,
                              height: 28,
                              bgcolor: isDealership
                                ? '#3b82f6'
                                : isStopCompleted
                                  ? 'success.main'
                                  : 'secondaryAccent.main',
                              fontSize: 13,
                              fontWeight: 700,
                            }}
                          >
                            {displayOrder}
                          </Avatar>
                          <Typography variant='body2' sx={{ fontWeight: 600, flex: 1 }}>
                            {detail?.customer_name || stop.notes || loc?.name || 'Unknown'}
                          </Typography>
                          {/* Dealership bookends carry no meaningful status — no chip */}
                          {!isDealership ? (
                            <Chip
                              label={isAddedNew ? 'New Stop' : (stopStatusLabels[stop.status] || formatStatus(stop.status))}
                              size='small'
                              color={isAddedNew ? 'success' : ((stopStatusColors[stop.status] || 'default') as any)}
                              sx={{ fontSize: 10, fontWeight: 600, height: 20 }}
                            />
                          ) : null}
                        </Box>

                        {/* Edit mode: why this card can't move */}
                        {runEdit.editing && isLockedStop ? (
                          <Typography variant='caption' color='text.disabled' sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 5.25, mb: 0.75, fontWeight: 600 }}>
                            <BiLockAlt size={12} />
                            {formatStatus(stop.status)} — locked
                          </Typography>
                        ) : null}
                        {runEdit.editing && isDealership && !isLockedStop ? (
                          <Typography variant='caption' color='text.disabled' sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 5.25, mb: 0.75 }}>
                            <BiLockAlt size={12} />
                            {idx === 0 ? 'Always first — tied to the dealership' : 'Always last — tied to the dealership'}
                          </Typography>
                        ) : null}

                        {/* Location name + Address */}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75, ml: 5.25 }}>
                          <BiMap size={14} color={theme.palette.text.secondary} style={{ marginTop: 2, flexShrink: 0 }} />
                          <Box>
                            {loc?.name ? (
                              <Typography variant='caption' sx={{ display: 'block', fontWeight: 500, color: 'text.primary' }}>
                                {loc.name}
                              </Typography>
                            ) : null}
                            <Typography variant='caption' color='text.secondary'>
                              {address}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Appointment window */}
                        {detail?.service_window ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, ml: 5.25 }}>
                            <BiCalendar size={14} color={theme.palette.text.secondary} />
                            <Typography variant='caption' color='text.secondary'>
                              Appt: {detail.service_window}
                            </Typography>
                          </Box>
                        ) : null}

                        {/* Phone */}
                        {detail?.customer_phone ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, ml: 5.25 }}>
                            <BiPhone size={14} color={theme.palette.text.secondary} />
                            <Typography variant='caption' color='text.secondary'>
                              {detail.customer_phone}
                            </Typography>
                          </Box>
                        ) : null}

                        {/* Vehicle */}
                        {order ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, ml: 5.25 }}>
                            <BiCar size={14} color={theme.palette.text.secondary} />
                            <Typography variant='caption' color='text.secondary'>
                              {[order.vehicle_year, order.vehicle_make, order.vehicle_model].filter(Boolean).join(' ')}
                              {order.vehicle_vin ? ` · ${order.vehicle_vin}` : ''}
                            </Typography>
                          </Box>
                        ) : null}

                        {/* Service description */}
                        {detail?.service_description ? (
                          <Typography variant='caption' color='text.secondary' sx={{ display: 'block', ml: 5.25, mt: 0.5, fontStyle: 'italic' }}>
                            {detail.service_description}
                          </Typography>
                        ) : null}

                        {/* Timing details */}
                        {(stop.geofence_arrived_at || stop.geofence_departed_at || detail?.service_started_at || detail?.service_completed_at) ? (
                          <Box sx={{ ml: 5.25, mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            {stop.geofence_arrived_at ? (
                              <Typography variant='caption' color='text.secondary'>
                                Arrived: {formatTime(stop.geofence_arrived_at)}
                              </Typography>
                            ) : null}
                            {detail?.service_started_at ? (
                              <Typography variant='caption' color='text.secondary'>
                                Started: {formatTime(detail.service_started_at)}
                              </Typography>
                            ) : null}
                            {detail?.service_completed_at ? (
                              <Typography variant='caption' color='success.main'>
                                Done: {formatTime(detail.service_completed_at)}
                              </Typography>
                            ) : null}
                          </Box>
                        ) : null}

                        {/* Estimated service time */}
                        {detail?.estimated_service_minutes ? (
                          <Typography variant='caption' color='text.disabled' sx={{ display: 'block', ml: 5.25, mt: 0.5 }}>
                            Est. ~{detail.estimated_service_minutes} min
                          </Typography>
                        ) : null}

                      </Box>

                      {/* Edit mode tools: edit + move up/down + remove */}
                      {runEdit.editing && canMove ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25, pr: 1, flexShrink: 0 }}>
                          {/* New stops mirror their appointment — edit the
                              appointment itself rather than the staged copy. */}
                          {!isAddedNew ? (
                            <IconButton
                              size='small'
                              aria-label='Edit stop'
                              onClick={() => setEditingStopFormId(prev => (prev === stop.id ? null : stop.id))}
                              sx={editingStopFormId === stop.id ? { color: 'primary.main' } : undefined}
                            >
                              <BiPencil size={14} />
                            </IconButton>
                          ) : null}
                          <IconButton
                            size='small'
                            aria-label='Move up'
                            disabled={idx === firstMovable}
                            onClick={() => runEdit.moveStop(stop.id, 'up')}
                          >
                            <BiChevronUp size={16} />
                          </IconButton>
                          <IconButton
                            size='small'
                            aria-label='Move down'
                            disabled={idx === lastMovable}
                            onClick={() => runEdit.moveStop(stop.id, 'down')}
                          >
                            <BiChevronDown size={16} />
                          </IconButton>
                          <IconButton
                            size='small'
                            aria-label='Remove stop'
                            onClick={() => {
                              if (editingStopFormId === stop.id) setEditingStopFormId(null);
                              runEdit.removeStop(stop.id);
                            }}
                            sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: theme.palette.error.light + '20' } }}
                          >
                            <BiTrash size={15} />
                          </IconButton>
                        </Box>
                      ) : null}
                     </Box>
                     )}
                    </Card>
                  );
                })}
              </Box>

              {/* Add stop from the pending-appointment queue (phase 3) */}
              {runEdit.editing ? (
                <Button
                  fullWidth
                  onClick={() => setAddStopPickerOpen(true)}
                  startIcon={<BiPlus size={15} />}
                  sx={{
                    mt: 1.5,
                    py: 1.25,
                    border: '1px dashed',
                    borderColor: `${theme.palette.primary.main}80`,
                    borderRadius: 2.5,
                    color: 'primary.main',
                    bgcolor: `${theme.palette.primary.main}0a`,
                    fontWeight: 600,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: `${theme.palette.primary.main}16`,
                    },
                  }}
                >
                  Add stop
                </Button>
              ) : null}
            </Box>
           </Box>

            {/* ── Edit mode footer: dirty count + discard/review ── */}
            {runEdit.editing ? (
              <Box sx={{
                flexShrink: 0,
                px: 2.5,
                py: 1.5,
                borderTop: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                bgcolor: 'background.paper',
              }}>
                <Typography variant='caption' color='text.secondary' sx={{ flex: 1 }}>
                  {runEdit.dirtyCount > 0 ? (
                    <>
                      <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {runEdit.dirtyCount}
                      </Box>
                      {` pending change${runEdit.dirtyCount === 1 ? '' : 's'}`}
                    </>
                  ) : (
                    'No changes yet'
                  )}
                </Typography>
                <Button size='small' variant='outlined' onClick={runEdit.discard}>
                  Discard
                </Button>
                <Button
                  size='small'
                  variant='contained'
                  color='primary'
                  disabled={runEdit.dirtyCount === 0}
                  onClick={() => setReviewOpen(true)}
                >
                  Review changes
                </Button>
              </Box>
            ) : null}
          </Box>

          {/* ── Right panel: map ── */}
          <Box sx={{ flex: 1, position: 'relative' }}>
            {/* Route preview tag — the dashed/straight-line draft route isn't
                the optimized route; make that explicit while editing. */}
            {runEdit.editing ? (
              <Box sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: 'primary.main',
                color: '#fff',
                px: 1.5,
                py: 0.75,
                borderRadius: 1.5,
                fontSize: 12,
                fontWeight: 700,
                boxShadow: '0px 2px 16px 2px rgba(32,36,48,0.16)',
              }}>
                <BiShow size={14} />
                Preview
              </Box>
            ) : null}
            <RunMapView
              mapId={MAP_ID}
              stops={mapStops}
              legPolylines={runEdit.editing ? [] : legPolylines}
              defaultCenter={defaultCenter}
              defaultZoom={12}
              defaultBounds={mapBounds || undefined}
              flyToBounds={flyToBounds}
              selectedStopIndex={selectedStopOrder !== null
                ? mapStops.findIndex(s => s.stopOrder === selectedStopOrder)
                : null
              }
              showGeofences
            >
              {/* Live driver position — subscribes to the most recent
                  driverlocations row for this run and renders a single
                  marker above the stop pins (zIndex 25 > stop flags at 20). */}
              {run?.id ? <RunDriverMarker runId={run.id} mapId={MAP_ID} /> : null}
            </RunMapView>
          </Box>
        </Box>
      ) : null}
    </>
  );
};

export default RunDetailPage;
